import { ref, watch, onMounted } from 'vue'

export function useTheme() {
  const isDark = ref(false)
  
  // 主题模式: 'auto' | 'dark' | 'light'
  const themeMode = ref<'auto' | 'dark' | 'light'>('auto')

  // 根据模式应用主题
  function applyTheme() {
    if (themeMode.value === 'auto') {
      // 自动模式：跟随系统主题
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      isDark.value = systemDark
      updateDOMClass(systemDark)
    } else {
      // 手动模式：使用用户设置的主题
      isDark.value = themeMode.value === 'dark'
      updateDOMClass(isDark.value)
    }
  }

  // 更新 DOM 的 class
  function updateDOMClass(dark: boolean) {
    if (dark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // 切换主题模式
  function toggleDark() {
    // 循环切换: auto -> dark -> light -> auto
    if (themeMode.value === 'auto') {
      themeMode.value = 'dark'
    } else if (themeMode.value === 'dark') {
      themeMode.value = 'light'
    } else {
      themeMode.value = 'auto'
    }
    applyTheme()
    saveTheme()
  }

  // 设置特定主题
  function setTheme(mode: 'auto' | 'dark' | 'light') {
    themeMode.value = mode
    applyTheme()
    saveTheme()
  }

  // 保存主题设置到 localStorage
  function saveTheme() {
    localStorage.setItem('bookmark-theme-mode', themeMode.value)
  }

  // 从 localStorage 加载主题设置
  function loadTheme() {
    const savedMode = localStorage.getItem('bookmark-theme-mode') as 'auto' | 'dark' | 'light' | null
    if (savedMode && ['auto', 'dark', 'light'].includes(savedMode)) {
      themeMode.value = savedMode
    }
  }

  // 监听系统主题变化
  function handleSystemThemeChange(e: MediaQueryListEvent) {
    if (themeMode.value === 'auto') {
      isDark.value = e.matches
      updateDOMClass(e.matches)
    }
  }

  // 初始化
  function init() {
    loadTheme()
    applyTheme()

    // 监听系统主题变化
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', handleSystemThemeChange)

    // 组件卸载时移除监听器（在 onMounted 中处理）
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }

  // 监听主题模式变化
  watch(themeMode, () => {
    applyTheme()
    saveTheme()
  })

  return {
    isDark,
    themeMode,
    toggleDark,
    setTheme,
    init
  }
}
